import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { withLatestFrom, switchMap, tap, map, catchError, of } from 'rxjs';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { IAuroraState } from '@apps/aurora.state';
import { getBckpVbrJobsExportQueryString, getBckpVbrJobsQueryString } from '@apps/bckp/store/bckp.reducer';
import { getAllTags } from '@apps/cmdb/store';
import { ResultDomain } from '@common/domain/result-domain';
import { NotifType } from '@common/enum';
import { FilesService, NotfGravity, NotificationService } from '@common/services';
import { BckpVbrJobsCleared, BckpVbrJobsExportRequested, BckpVbrJobsExportSucceeded, BckpVbrJobsFilterApplied, BckpVbrJobsODataFailed, BckpVbrJobsODataInitialized, BckpVbrJobsODataLoaded, BckpVbrJobsODataRequested } from './vbr-jobs.actions';
import { VeeamJob } from '@apps/bckp/domain/models/veeamJob';
import { VbrJobListDomainService } from '@apps/bckp/domain/veeam/vbr/vbr-job-list-domain.service';
import { BckpConstants } from '@apps/bckp/bckp.constants';
import { Profile } from '@apps/auth/models';
import { getProfile } from '@apps/auth/store';
import { VbrJobMiscDomainService } from '@apps/bckp/domain/veeam/vbr/vbr-job-misc-domain.service';

@Injectable()
export class BckpVbrJobEffects
{
    private jobsCount: number;

    constructor (
        private store: Store<IAuroraState>,
        private notificationService: NotificationService,
        private actions: Actions,
        private jobListDomainService: VbrJobListDomainService,
        private filesService: FilesService,
        private vbrJobMiscDomainService: VbrJobMiscDomainService
    )
    { }

    bckpVbrJobsODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BckpVbrJobsODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpVbrJobsQueryString),
                        this.store.select(getAllTags),
                        (_, qs, allTags) => ({
                            qs,
                            allTags
                        })
                    ),
                    switchMap(({ qs, allTags }) =>
                        this.jobListDomainService.getAll(qs)
                            .pipe(
                                tap((dataLoaded: ResultDomain<VeeamJob>) =>
                                {
                                    this.jobsCount = dataLoaded.count;
                                }),
                                map((obj: ResultDomain<VeeamJob>): VeeamJob[] => obj.value),
                                //joinAssets(this.assetMiscDomainService, TypeAssetName.HOST, allTags),
                                map((jobs: VeeamJob[]) =>
                                    BckpVbrJobsODataLoaded({
                                        dataLoaded: {
                                            value: jobs,
                                            count: this.jobsCount
                                        } as ResultDomain<VeeamJob>
                                    })
                                ),
                                catchError(() =>
                                    of(BckpVbrJobsODataFailed())
                                )
                            )
                    )
                )
    );

    bckpVbrJobsODataFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BckpVbrJobsODataFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.JOBS.NOTF.LIST.TITLE',
                                'BACKUP.JOBS.NOTF.LIST.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpVbrJobsFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BckpVbrJobsFilterApplied),
                    tap((params: any) =>
                    {
                        this.store.dispatch(BckpVbrJobsCleared());
                        this.store.dispatch(BckpVbrJobsODataInitialized({
                            scrollSize: BckpConstants.QSJobsTop,
                            filter: params.filter
                        }));
                    }),
                    tap(() =>
                        this.store.dispatch(BckpVbrJobsODataRequested())
                    )
                ),
        { dispatch: false }
    );

    bckpVbrJobsExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BckpVbrJobsExportRequested),
                    withLatestFrom(this.store.select(getBckpVbrJobsExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.vbrJobMiscDomainService.export(qs, params.format)
                    ),
                    map((response: HttpEvent<Blob>) =>
                        BckpVbrJobsExportSucceeded({ response })
                    )
                )
    );

    bckpVbrJobsExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(BckpVbrJobsExportSucceeded),
                    withLatestFrom(this.store.select(getProfile)),
                    tap(([params, profile]: [{ response: HttpEvent<Blob> }, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );
}
